import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/uiDir/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/uiDir/avatar";
import { Badge } from "@/components/uiDir/badge";
import { Button } from "@/components/uiDir/button";
import { Input } from "@/components/uiDir/input";
import { Search, Eye, Heart, ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { ExploreModel } from "@/lib/models/exploreModel";
import { Portfolio } from "@/lib/models/exploreModel";

const Explore = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [filteredPortfolios, setFilteredPortfolios] = useState<Portfolio[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const programs = [
    { value: "COMSCI", label: "Computer Science" },
    { value: "BMMA", label: "BMMA" },
    { value: "FILM", label: "Film & Visual Media" },
    { value: "INFOSYSTEM", label: "Information Systems" },
    { value: "ENTREPRENEURSHIP", label: "Entrepreneurship" },
  ];

  const programColors: Record<string, string> = {
    "COMSCI": "bg-primary/10 text-primary border-primary/20",
    "BMMA": "bg-secondary/10 text-secondary border-secondary/20",
    "FILM": "bg-accent/10 text-accent border-accent/20",
    "INFOSYSTEM": "bg-success/10 text-success border-success/20",
    "ENTREPRENEURSHIP": "bg-destructive/10 text-destructive border-destructive/20",
  };

  useEffect(() => {
    fetchPortfolios();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!isMounted) return;
      setIsAuthenticated(!!session);
    };
    checkSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setIsAuthenticated(!!session);
    });
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterPortfolios();
  }, [searchQuery, selectedProgram, portfolios]);


  const fetchPortfolios = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, full_name, program, title, avatar_url, profile_views, total_likes, projects_count')
        .order('profile_views', { ascending: false })
        .limit(50);

      if (error) throw error;

      setPortfolios(data || []);
      setFilteredPortfolios(data || []);
    } catch (err) {
      console.error('Error fetching portfolios:', err);
      toast.error('Failed to load portfolios');
    } finally {
      setIsLoading(false);
    }
  };

  const filterPortfolios = () => {
    let filtered = [...portfolios];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.full_name.toLowerCase().includes(query) ||
        p.username.toLowerCase().includes(query) ||
        p.program.toLowerCase().includes(query) ||
        (p.title && p.title.toLowerCase().includes(query))
      );
    }

    // Filter by program
    if (selectedProgram) {
      filtered = filtered.filter(p => p.program === selectedProgram);
    }

    setFilteredPortfolios(filtered);
  };

  const handleProgramFilter = (program: string | null) => {
    setSelectedProgram(selectedProgram === program ? null : program);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading portfolios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <header
        className="bg-white/80 border-b border-gray-200 sticky z-40 backdrop-blur-lg"
        style={{ top: "var(--beta-banner-height)" }}
      >
        <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link
            to={isAuthenticated ? "/dashboard" : "/"}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {isAuthenticated ? "Back to Dashboard" : "Back to Home"}
          </Link>
          <h1 className="text-xl font-bold text-primary bg-clip-text">
            Explore Portfolios
          </h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="max-w-4xl mx-auto mb-8 sm:mb-10">
          <div className="relative mb-5 sm:mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input 
              placeholder="Search by name, program, or skills..." 
              className="pl-10 h-12 text-base border-gray-300"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Badge 
              variant={selectedProgram === null ? "default" : "outline"}
              className="cursor-pointer hover:bg-opacity-80"
              onClick={() => handleProgramFilter(null)}
            >
              All Programs
            </Badge>
            {programs.map((program) => (
              <Badge 
                key={program.value}
                variant={selectedProgram === program.value ? "default" : "outline"}
                className="cursor-pointer hover:bg-opacity-80"
                onClick={() => handleProgramFilter(program.value)}
              >
                {program.label}
              </Badge>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="max-w-7xl mx-auto mb-4 sm:mb-6">
          <p className="text-gray-600">
            Showing {filteredPortfolios.length} {filteredPortfolios.length === 1 ? 'portfolio' : 'portfolios'}
          </p>
        </div>

        {/* Portfolio Grid */}
        {filteredPortfolios.length === 0 ? (
          <div className="text-center py-10 sm:py-12">
            <p className="text-gray-600 text-lg">No portfolios found</p>
            <p className="text-gray-500 text-sm mt-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-7xl mx-auto">
            {filteredPortfolios.map((portfolio, index) => (
              <Link key={portfolio.id} to={`/profile/${portfolio.username}`}>
                <Card 
                  className="shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-0"
                >
                  <CardContent className="p-5 sm:p-6">
                    {/* Avatar and Name */}
                    <div className="flex items-center gap-4 mb-4">
                      <Avatar className="h-14 w-14 border-2 border-purple-600">
                        <AvatarImage 
                          src={portfolio.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${portfolio.username}`} 
                        />
                        <AvatarFallback>
                          {portfolio.full_name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{portfolio.full_name}</h3>
                        <p className="text-sm text-gray-600">@{portfolio.username}</p>
                      </div>
                    </div>

                    {/* Program Badge */}
                    <Badge 
                      className={`mb-3 ${programColors[portfolio.program] || "bg-gray-100"}`}
                      variant="outline"
                    >
                      {programs.find(p => p.value === portfolio.program)?.label || portfolio.program}
                    </Badge>

                    {/* Tagline */}
                    {portfolio.title && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {portfolio.title}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          {portfolio.profile_views}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="h-4 w-4" />
                          {portfolio.total_likes}
                        </span>
                      </div>
                      <span className="text-sm font-medium">
                        {portfolio.projects_count} projects
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Load More - Di pa gumagana hehe*/}
        {filteredPortfolios.length > 0 && filteredPortfolios.length >= 50 && (
          <div className="text-center mt-12">
            <Button variant="outline" size="lg">
              Load More Portfolios
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Explore;

